import {
  type CommentMentionInboxItem,
  type InboxItem,
  InboxItemType,
  type NewCommentInboxItem,
  type NewCommentReplyInboxItem,
} from '@seen/core/models/inbox'

export const INBOX_CATEGORY_IDS = [
  'all',
  'stat',
  'overdue',
  'orders',
  'documents',
  'scheduling',
  'discussions',
  'medications',
  'other-tasks',
] as const

export type InboxCategoryId = (typeof INBOX_CATEGORY_IDS)[number]

export interface InboxCategoryConfig {
  id: InboxCategoryId
  label: string
  /** Plural noun for read-filtered empty states, e.g. "No unread {filterNoun}" */
  filterNoun: string
  emptyMessage: string
}

export const INBOX_CATEGORIES: InboxCategoryConfig[] = [
  { id: 'all', label: 'All', filterNoun: 'items', emptyMessage: '' },
  {
    id: 'stat',
    label: 'STAT',
    filterNoun: 'stat items',
    emptyMessage: 'No stat items',
  },
  {
    id: 'overdue',
    label: 'Overdue',
    filterNoun: 'overdue items',
    emptyMessage: 'No overdue items',
  },
  {
    id: 'orders',
    label: 'Orders',
    filterNoun: 'orders',
    emptyMessage: 'No orders to review',
  },
  {
    id: 'documents',
    label: 'Document Review',
    filterNoun: 'document review items',
    emptyMessage: 'No document review items',
  },
  /* Scheduling: NewProposedAppointment notifications + comments on Appointments */
  {
    id: 'scheduling',
    label: 'Scheduling',
    filterNoun: 'scheduling items',
    emptyMessage: 'No scheduling items',
  },
  {
    id: 'discussions',
    label: 'Discussions',
    filterNoun: 'discussions',
    emptyMessage: 'No new discussions',
  },
  {
    id: 'medications',
    label: 'Medications',
    filterNoun: 'medication tasks',
    emptyMessage: 'No medication tasks',
  },
  {
    id: 'other-tasks',
    label: 'Other Tasks',
    filterNoun: 'other tasks',
    emptyMessage: 'No other tasks',
  },
]

/* Curated phrases that indicate a medication/pharmacy task. Matched
   case-insensitively on word boundaries against task title + description.
   Tune this list as false positives/negatives surface — it is the single
   source of truth for the Medications heuristic. */
const MEDICATION_KEYWORDS = [
  'prescription',
  'prescriptions',
  'rx',
  'e-rx',
  'erx',
  'refill',
  'refills',
  'medication',
  'medications',
  'meds',
  'pharmacy',
  'e-prescribe',
  'eprescribe',
] as const

const MEDICATION_REGEX = new RegExp(
  `\\b(${MEDICATION_KEYWORDS.map((k) =>
    k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  ).join('|')})\\b`,
  'i'
)

/* Heuristic: does any of the given task text fields look medication-related?
   Conservative by design — see MEDICATION_KEYWORDS. */
export function matchesMedicationHeuristic(
  ...texts: (string | undefined | null)[]
): boolean {
  return texts.some((t) => !!t && MEDICATION_REGEX.test(t))
}

export const TYPE_BASED_CATEGORY_MAP: Partial<
  Record<InboxItemType, InboxCategoryId>
> = {
  [InboxItemType.OrderSigned]: 'orders',
  [InboxItemType.NewProposedAppointment]: 'scheduling',
  [InboxItemType.FaxError]: 'other-tasks',
}

export const TASK_BASED_TYPES = new Set<InboxItemType>([
  InboxItemType.TaskAssigned,
  InboxItemType.TaskDue,
  InboxItemType.TaskNewComment,
])

export const COMMENT_TYPES = new Set<InboxItemType>([
  InboxItemType.CommentMention,
  InboxItemType.NewComment,
  InboxItemType.NewCommentReply,
])

export function isTaskBasedType(type: InboxItemType): boolean {
  return TASK_BASED_TYPES.has(type)
}

export function isCommentType(
  item: InboxItem
): item is
  | CommentMentionInboxItem
  | NewCommentInboxItem
  | NewCommentReplyInboxItem {
  return COMMENT_TYPES.has(item.type)
}

interface PartOfResourceTypeCategoryMapLookup {
  [key: string]: InboxCategoryId
}

export const PART_OF_RESOURCE_TYPE_CATEGORY_MAP: PartOfResourceTypeCategoryMapLookup =
  {
    ServiceRequest: 'orders',
    DocumentReference: 'documents',
    Appointment: 'scheduling',
  }

export function getCategoryForPartOfResourceType(
  partOfResourceType: string | undefined
): InboxCategoryId {
  if (!partOfResourceType) {
    return 'discussions'
  }
  return PART_OF_RESOURCE_TYPE_CATEGORY_MAP[partOfResourceType] ?? 'discussions'
}
